import { UIValue } from '../ui/types';
import { $api } from '../y/api';
import { makeDataStore } from '../y/data-store';
import { $fn } from '../y/fn';
import { YI18NString, YLanguage } from '../y/i18n';
import { YFlags, YId, YTimeString } from '../y/types';
import { MChannel } from './m-message';

export type MContext = 'user' | 'booking' | 'reservation' | 'subscription' | 'giftcard' | 'invite' | 'b2b_giftcard' | 'purchase' | 'producer' | 'producer_invite' | 'booking_list' | 'producer_invoice' | 'producer_event' | 'reset_password' | 'otp';
export type MLink = 'my_bookings' | 'my_purchases' | 'membership' | 'giftcard' | 'invite' | 'redeem_giftcard' | 'home' | 'event' | 'booking' | 'support_link' | 'invite_link' | 'producer_home' | 'producer_events' | 'booking_list' | 'producer_invoices' | 'past_30' | 'reset_password';
export type MField = 'user.first_name' | 'user.full_name' | 'b2b_giftcard.name' | 'b2b_giftcard.email' | 'b2b_giftcard.company' | 'b2b_giftcard.plan' | 'b2b_giftcard.months' | 'b2b_giftcard.quantity' | 'reserving_user.first_name' | 'reserving_user.full_name' | 'event.name' | 'event.url' | 'venue.name' | 'event.booking_emails' | 'session.time' | 'session.unbook_end' | 'session.ticket' | 'booking.token_number' | 'booking.amount' | 'booking.expiration' | 'video.time' | 'producer.name' | 'country.host' | 'country.support_email' | 'country.late_cancellation_fee' | 'invite.bonus' | 'invite.name' | 'stats.active_sessions' | 'date.today' | 'date.tomorrow' | 'booking_list.total_booked' | 'booking_list.title' | 'booking_list.session_time' | 'invoice.period' | 'gc.buyer_name' | 'gc.buyer_email' | 'gc.date' | 'gc.delivery_name' | 'gc.delivery_address' | 'gc.delivery_postcode' | 'gc.delivery_city' | 'gc.expiration' | 'transaction.date' | 'transaction.amount' | 'transaction.method' | 'transaction.effective_date' | 'subscription.plan' | 'subscription.last_date' | 'subscription.next_payment_date' | 'subscription.next_payment_amount' | 'country.standard_price' | 'otp.code';
export type MEmailFrom = 'noreply' | 'info' | 'system' | 'support' | 'producer_personal';
export type MEmailAttachment = 'ical' | 'giftcard' | 'tickets' | 'receipt' | 'booking_list';

export interface MEmailTextBlock {
    type: 'text';
    text: YI18NString;
    columns: boolean;
}

export interface MEmailCalendarBlock {
    type: 'calendar';
}

export interface MEmailHRBlock {
    type: 'hr';
}

export interface MEmailMapBlock {
    type: 'map';
}

export interface MEmailInstructionsBlock {
    type: 'instructions';
}

export interface MEmailButtonBlock {
    type: 'button';
    text: YI18NString;
    link_type: 'url' | 'link';
    url?: string;
    link?: string;
}

export interface MEmailImage {
    image: YId;
    width: number;
    height: number;
    aspect: number;
}

export interface MEmailSessionListBlock {
    type: 'session_list';
}

export type MEmailBlock = MEmailTextBlock | MEmailHRBlock | MEmailCalendarBlock | MEmailMapBlock | MEmailInstructionsBlock | MEmailButtonBlock | MEmailSessionListBlock;

export interface MEmailHeader {
    image: '' | 'data' | 'event' | 'custom';
    image_event?: YId;
    image_custom?: MEmailImage;
    button: boolean;
    button_text?: YI18NString;
    button_link?: MLink;
}

export interface MEmailFooter {
    unsubscribe: boolean;
}

export interface MEmailSection {
    background: string;
    blocks: MEmailBlock[];
}

export interface MEmailContent {
    from: MEmailFrom;
    subject: YI18NString;
    header: MEmailHeader;
    footer: MEmailFooter;
    sections?: MEmailSection[];
    decoration: string;
    schema: boolean;
    attachments: MEmailAttachment[];
}

export interface MInboxContent {
    body: YI18NString;
}

export interface MPushContent {
    title: YI18NString;
    body: YI18NString;
    link?: MLink;
}

export interface MSMSContent {
    body: YI18NString;
}

export interface MTemplateBase {
    id: YId;
    name: string;
    owner_user_id: YId;
    channel: MChannel;
    resource_id?: string;
    is_temporary: boolean;
    is_hidden: boolean;
    is_locked: boolean;
    context: MContext;
    languages: YFlags<YLanguage>;
    created_at: YTimeString;
    updated_at: YTimeString;
}

export interface MEmailTemplate extends MTemplateBase {
    channel: 'email';
    content: MEmailContent;
}

export interface MSMSTemplate extends MTemplateBase {
    channel: 'sms';
    content: MSMSContent;
}

export interface MPushTemplate extends MTemplateBase {
    channel: 'push';
    content: MPushContent;
}

export interface MInboxTemplate extends MTemplateBase {
    channel: 'inbox';
    content: MInboxContent;
}

export type MTemplate = MEmailTemplate | MSMSTemplate | MPushTemplate | MInboxTemplate;

export const m_templates = makeDataStore<YId, MTemplate, MTemplate>('m/templates', 'm/template', $fn.ID, Object.assign)

export function mTemplateFieldsForContext(context: MContext): [UIValue<MField>[], UIValue<MLink>[]] {
    const fields: UIValue<MField>[] = [];
    const links: UIValue<MLink>[] = [{ key: 'giftcard', name: 'Gift card' }, { key: 'support_link', name: 'Support link' }];

    if(context == 'user' || context == 'booking' || context == 'reservation' || context == 'subscription' || context == 'invite' || context == 'purchase' || context == 'producer_invoice' || context == 'reset_password') {
        fields.push(
            { key: 'user.first_name', name: 'User first name' },
            { key: 'user.full_name', name: 'User full name' },
        )
    }

    if(context == 'b2b_giftcard') {
        fields.push(
            { key: 'b2b_giftcard.name', name: 'B2B info name' },
            { key: 'b2b_giftcard.email', name: 'B2B info email' },
            { key: 'b2b_giftcard.company', name: 'B2B info company' },
            { key: 'b2b_giftcard.plan', name: 'B2B info plan' },
            { key: 'b2b_giftcard.months', name: 'B2B info months' },
            { key: 'b2b_giftcard.quantity', name: 'B2B info quantity' }
        );
    }

    if(context == 'reservation') {
        fields.push(
            { key: 'reserving_user.first_name', name: 'Reserving user first name' },
            { key: 'reserving_user.full_name', name: 'Reserving user full name' },
        )
    }

    if(context == 'invite') {
        fields.push(
            { key: 'invite.name', name: 'Invited user name' },
            { key: 'country.standard_price', name: 'Standard price' },
        )

        links.push({ key: 'invite_link', name: 'Invite link' });
    }

    if(context == 'producer_invite') {
        links.push({ key: 'invite_link', name: 'Invite link' });
    }

    if(context == 'booking' || context == 'reservation' || context == 'purchase' || context == 'producer_event') {
        fields.push(
            { key: 'event.name', name: 'Event name' },
            { key: 'event.url', name: 'Event url' },
        )

        links.push({ key: 'event', name: 'Event' })
    }

    if(context == 'producer_event') {
        fields.push(
            { key: 'venue.name', name: 'Venue name' },
            { key: 'event.booking_emails', name: 'Booking emails' },
        )
    }

    if(context == 'booking' || context == 'reservation') {
        fields.push(
            { key: 'venue.name', name: 'Venue name' },
            { key: 'session.time', name: 'Session time' },
            { key: 'session.unbook_end', name: 'Session unbook end' },
            { key: 'session.ticket', name: 'Session ticket type' },
            { key: 'booking.token_number', name: 'Booking token number' },
            { key: 'booking.amount', name: 'Booking amount' },
            { key: 'booking.expiration', name: 'Booking expiration' },
        )

        links.push({ key: 'booking', name: 'Booking' })
    }

    if(context == 'purchase') {
        fields.push(
            { key: 'video.time', name: 'Video time' },
        )
    }

    if(context == 'reset_password') {
        links.push({ key: 'reset_password', name: 'Reset password' });
    }

    if(context == 'producer' || context == 'booking_list' || context == 'producer_invoice' || context == 'producer_event' || context == 'producer_invite') {
        fields.push(
            { key: 'producer.name', name: 'Producer name' },
        )

        links.push({ key: 'producer_home', name: 'Producer home' }, { key: 'producer_events', name: 'Producer events' }, { key: 'producer_invoices', name: 'Producer invoices' });
    } else {
        links.push({ key: 'home', name: 'Homepage' }, { key: 'my_bookings', name: 'My bookings' }, { key: 'my_purchases', name: 'My purchases' }, { key: 'membership', name: 'Membership' }, { key: 'invite', name: 'Invite friends' }, { key: 'past_30', name: 'Past events 30 days' });
    }

    if(context == 'booking_list') {
        fields.push(
            { key: 'booking_list.total_booked', name: 'Total booked' },
            { key: 'booking_list.title', name: 'Booking list title' },
            { key: 'booking_list.session_time', name: 'Booking list session time' },
        )

        links.push({ key: 'booking_list', name: 'Booking list' });
    }

    if(context == 'producer_invoice') {
        fields.push(
            { key: 'invoice.period', name: 'Invoice period' },
        )
    }

    if(context == 'giftcard') {
        fields.push(
            { key: 'gc.buyer_name', name: 'Giftcard buyer name' },
            { key: 'gc.buyer_email', name: 'Giftcard buyer email' },
            { key: 'gc.date', name: 'Giftcard date' },
            { key: 'gc.delivery_name', name: 'Giftcard delivery name' },
            { key: 'gc.delivery_address', name: 'Giftcard delivery address' },
            { key: 'gc.delivery_postcode', name: 'Giftcard delivery post code' },
            { key: 'gc.delivery_city', name: 'Giftcard delivery city' },
            { key: 'gc.expiration', name: 'Giftcard expiration' },
        )

        links.push({ key: 'redeem_giftcard', name: 'Redeem gift card' });
    }

    if(context == 'subscription' || context == 'giftcard' || context == 'purchase') {
        fields.push(
            { key: 'transaction.date', name: 'Transaction date' },
            { key: 'transaction.amount', name: 'Transaction amount' },
            { key: 'transaction.method', name: 'Transaction method' },
            { key: 'transaction.effective_date', name: 'Transaction effective date' },
        )
    }

    if(context == 'subscription') {
        fields.push(
            { key: 'subscription.plan', name: 'Subscription plan' },
            { key: 'subscription.last_date', name: 'Subscription last date' },
            { key: 'subscription.next_payment_date', name: 'Subscription next payment date' },
            { key: 'subscription.next_payment_amount', name: 'Subscription next payment amount' },
        )
    }

    if(context == 'otp') {
        fields.push(
            { key: 'otp.code', name: 'OTP code' },
        )
    }

    fields.push(
        { key: 'country.host', name: 'Country host' },
        // { key: 'country.phone', name: 'Country phone' },
        { key: 'country.support_email', name: 'Country support email' },
        { key: 'country.late_cancellation_fee', name: 'Late cancellation fee' },
        { key: 'invite.bonus', name: 'Invite bonus' },
        { key: 'stats.active_sessions', name: 'Stats active sessions' },
        { key: 'date.today', name: 'Today\'s date' },
        { key: 'date.tomorrow', name: 'Tomorrow\'s date' }
    )

    return [fields, links];
}

export function mTemplateSendTest(template: YId, value: any) {
    return $api.POST(`m/template/${template}/test`, value);
}

export function mTemplateSendCustom(session: YId | undefined, user: YId | undefined, template: any) {
    return $api.POST('m/templates/send_custom', { session, user, template });
}
